import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios, {} from "axios";
import { useSearchParams } from "react-router-dom";
import { Alert, Modal, Spin, Tabs } from "antd";
import { axiosErrorHandler, isConcurrentModificationError, isErrorOverridable, isPaymentRequiredError } from "../../../../swzutils";
import FleetVehicleForm, {} from "../../components/FleetVehicleForm";
import PartnerVehicleForm, {} from "../../components/PartnerVehicleForm";
import StripePaymentIntentForm, { SEARCH_PARAM_PAYMENT_INTENT } from "../billing/StripePaymentIntentForm";
import { SHOW_PLACE_VEHICLE_HIRE_ON_MARKET_MODAL } from "./VehicleHireForm";
import { userHasPermission } from "../../../../swzutils/UserPermissionsUtil";
import RelationshipType from "../../enums/RelationshipType";
import { loggedInUserSelector } from "../../../../store/slice/auth";
import { useAppSelector } from "../../../../store";
export var VehicleHireAllocateTab;
(function (VehicleHireAllocateTab) {
    VehicleHireAllocateTab["FLEET"] = "FLEET";
    VehicleHireAllocateTab["PARTNER"] = "PARTNER";
})(VehicleHireAllocateTab || (VehicleHireAllocateTab = {}));
const VehicleHireAllocateVehicleModal = ({ open, vehicleHireId, vehicleHireTenantId, vehicleHireVersion, partnerTenantId, visibleTabs, onAllocated, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [activeTab, setActiveTab] = useState(VehicleHireAllocateTab.FLEET);
    const [showTabs, setShowTabs] = useState([VehicleHireAllocateTab.FLEET, VehicleHireAllocateTab.PARTNER]);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [errorIsOverridable, setErrorIsOverridable] = useState(false);
    const fleetVehicleFormRef = useRef(null);
    const partnerVehicleFormRef = useRef(null);
    const paymentFormRef = useRef(null);
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const [searchParams] = useSearchParams();
    useEffect(() => {
        if (open) {
            setErrorMessage(undefined);
            setErrorIsOverridable(false);
            fleetVehicleFormRef.current?.resetFields();
            partnerVehicleFormRef.current?.resetFields();
            const canAllocateToFleet = userHasPermission(loggedInUser, ['admin:vehicleHires:allocateToFleet']);
            const canAllocateToPartner = userHasPermission(loggedInUser, ['admin:vehicleHires:allocateToPartner']);
            let showTabs = [];
            if (canAllocateToFleet && visibleTabs.indexOf(VehicleHireAllocateTab.FLEET) >= 0) {
                showTabs.push(VehicleHireAllocateTab.FLEET);
            }
            if (canAllocateToPartner && visibleTabs.indexOf(VehicleHireAllocateTab.PARTNER) >= 0) {
                showTabs.push(VehicleHireAllocateTab.PARTNER);
            }
            setShowTabs(showTabs);
            if (showTabs.length) {
                setActiveTab(showTabs[0]);
            }
            if (searchParams.has(SEARCH_PARAM_PAYMENT_INTENT)) {
                // This query param is present in the stripe returnUrl so just show the payment form to poll for the result
                setShowPaymentForm(true);
            }
        }
    }, [open]);
    const onTabChanged = (activeKey) => {
        setActiveTab(activeKey);
    };
    const allocateToVehicle = () => {
        const activeForm = (VehicleHireAllocateTab.PARTNER === activeTab)
            ? partnerVehicleFormRef.current
            : fleetVehicleFormRef.current;
        activeForm?.validateFields().then((formValues) => {
            setLoading(true);
            const vehicleId = formValues.vehicleId; // both forms use same key for vehicle id
            let url = `/admin/v1/vehicle-hires/${vehicleHireId}/allocate/${vehicleId}?version=${vehicleHireVersion}`;
            if (errorIsOverridable) {
                url += '&forceCanCarry=true';
            }
            axios.put(url)
                .then(response => onAllocated(response.data))
                .catch(error => {
                if (isPaymentRequiredError(error)) {
                    setShowPaymentForm(true);
                }
                else if (isConcurrentModificationError(error)) {
                    setErrorMessage("The vehicle allocation could not be performed. This is because another user has modified the same vehicle request.  Please refresh the list and try again.");
                }
                else {
                    axiosErrorHandler(error, errorMessage => {
                        setErrorMessage(errorMessage);
                        setErrorIsOverridable(isErrorOverridable(error));
                    });
                }
            })
                .finally(() => setLoading(false));
        }).catch(errorInfo => console.error(errorInfo)); // This has to be present otherwise antd crashes :(
    };
    const onVehicleSelectionChanged = (vehicle) => {
        if (errorIsOverridable) {
            setErrorIsOverridable(false);
            setErrorMessage(undefined);
        }
    };
    const buildTabItems = (vehicleHireTenantId) => {
        const tabItems = [];
        if (showTabs.indexOf(VehicleHireAllocateTab.FLEET) >= 0) {
            tabItems.push({
                key: VehicleHireAllocateTab.FLEET,
                label: 'My Vehicles',
                children: _jsx(FleetVehicleForm, { ref: fleetVehicleFormRef, onVehicleSelected: onVehicleSelectionChanged })
            });
        }
        if (showTabs.indexOf(VehicleHireAllocateTab.PARTNER) >= 0) {
            tabItems.push({
                key: VehicleHireAllocateTab.PARTNER,
                label: "Contractor Partner",
                children: _jsx(PartnerVehicleForm, { sourceTenantId: vehicleHireTenantId, partnerTenantId: partnerTenantId, partnerRelationship: RelationshipType.CONTRACTOR, ref: partnerVehicleFormRef, onVehicleSelected: onVehicleSelectionChanged })
            });
        }
        return tabItems;
    };
    const tabItems = useMemo(() => {
        return vehicleHireTenantId ? buildTabItems(vehicleHireTenantId) : [];
    }, [showTabs, vehicleHireTenantId, errorIsOverridable, errorMessage]);
    const onCreatePaymentIntent = () => {
        return axios.post(`/admin/v1/vehicle-hires/${vehicleHireId}/marketplace/payment-intent`);
    };
    const onPurchaseComplete = () => {
        allocateToVehicle();
    };
    const onOkClicked = () => {
        if (showPaymentForm) {
            paymentFormRef.current?.submit();
        }
        else {
            allocateToVehicle();
        }
    };
    return (_jsx(_Fragment, { children: _jsx(Modal, { title: "Allocate Vehicle Request", open: open, onOk: onOkClicked, okText: errorIsOverridable ? 'Override' : (showPaymentForm ? 'Purchase' : 'Allocate'), onCancel: onCancel, confirmLoading: loading, children: _jsxs(Spin, { spinning: loading, children: [!showPaymentForm &&
                        _jsx(Tabs, { activeKey: activeTab, onChange: onTabChanged, items: tabItems }), open && showPaymentForm &&
                        _jsx(StripePaymentIntentForm, { ref: paymentFormRef, onCreatePaymentIntent: onCreatePaymentIntent, returnUrl: `/#/app/vehicle-hires/edit/${vehicleHireId}?${SHOW_PLACE_VEHICLE_HIRE_ON_MARKET_MODAL}=1`, onPaymentSuccess: onPurchaseComplete }), errorMessage &&
                        _jsx(Alert, { title: _jsxs(_Fragment, { children: [errorMessage, " ", errorIsOverridable ? _jsxs(_Fragment, { children: [_jsx("br", {}), _jsx("br", {}), "Select the [Override] button to continue allocating this vehicle"] }) : null] }), type: "error", banner: true })] }) }) }));
};
export default VehicleHireAllocateVehicleModal;
